import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer, SuiLabel } from '@splashthat/sui';
import ColorAndImagePicker from 'common/components/ColorAndImagePicker';
import DisplayModeEditor from 'common/components/DisplayModeEditor';
import ColorPickerEditor from 'common/components/ColorPicker';

import { getColors } from 'common/entities/color/selectors';
import {
    getFormColors,
    getFormBackgroundImage,
    getFormDisplayStyles,
    getFormCloseButtonStyles,
    getFormLinksStyles,
} from 'common/entities/form/styleConfiguration/selectors';
import {
    DISPLAY_MODE_STANDARD,
    DISPLAY_MODE_PANEL,
    PANEL_DISPLAY_RIGHT,
    PANEL_DISPLAY_LEFT,
    DISPLAY_MODE_FULL_SCREEN,
} from 'common/entities/form/styleConfiguration/constants';
import {
    setFormColorBackground,
    setFormBackgroundImageBaseUrl,
    setFormBackgroundImageUrl,
    setFormDisplayMode,
    setFormPanelDisplay,
    setFormCloseButtonColorDefault,
    setFormCloseButtonColorHover,
    setFormCloseButtonColorFocus,
    setFormLinksColorDefault,
    setFormLinksColorHover,
    setFormLinksColorFocus,
} from 'common/entities/form/styleConfiguration/actions';
import {
    DEFAULT_FORM_BACKGROUND_COLOR,
    DEFAULT_FORM_BUTTON_COLOR,
    DEFAULT_FORM_BUTTON_COLOR_HOVER,
    DEFAULT_FORM_BUTTON_COLOR_FOCUS,
    DEFAULT_FORM_LINKS_COLOR,
    DEFAULT_FORM_LINKS_COLOR_HOVER,
    DEFAULT_FORM_LINKS_COLOR_FOCUS,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { getFormType } from 'common/entities/form/selectors';
import { FORM_TYPE_RSVP } from 'common/entities/form';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';

import SpacingSection from './SpacingSection';

import { getFormId } from '../../../../selectors';
import {
    FORM_SIZE_LABEL,
    FORM_BACKGROUND_LABEL,
    CLOSE_BUTTON_LABEL,
    COLOR_PICKER_DEFAULT,
    COLOR_PICKER_HOVER,
    COLOR_PICKER_FOCUS,
    FORM_LINKS_LABEL,
} from '../../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../../constants/reusableClassNames';

import {
    FORM_QA,
    LINKS_LABEL_QA,
    SPACING_LABEL_QA,
    BACKGROUND_LABEL_QA,
    MODAL_SIZE_LABEL_QA,
    CLOSE_BUTTON_LABEL_QA,
} from '../../../../utils/qaIdConstants';

const FormStyleEditorFormPanel = ({
    formId,
    colorList,
    linksColorList,
    imageUrl,
    imageBaseUrl,
    displayMode,
    panelDisplay,
    ...props
}) => (
    <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${FORM_QA}--panel`}>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${FORM_QA}--section-${MODAL_SIZE_LABEL_QA}`}>
            <SuiLabel text={FORM_SIZE_LABEL} data-qa={`${FORM_QA}--label`} />
            <DisplayModeEditor
                displayMode={displayMode}
                panelDisplay={panelDisplay}
                onChangeDisplayMode={(size) => props.setFormDisplayMode(formId, size)}
                onChangePanelDisplay={(size) => props.setFormPanelDisplay(formId, size)}
                qaId={`${FORM_QA}--modal-size`}
            />
        </SuiContainer>
        {props.isRsvpEvent && (
            <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${FORM_QA}--section-${SPACING_LABEL_QA}`}>
                <SpacingSection />
            </SuiContainer>
        )}
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${FORM_QA}--section-${BACKGROUND_LABEL_QA}`}>
            <SuiLabel text={FORM_BACKGROUND_LABEL} data-qa={`${FORM_QA}--label`} />
            <ColorAndImagePicker
                colorList={colorList}
                color={props.background}
                changeColor={(id) => props.setFormColorBackground(formId, id)}
                qaId={`${FORM_QA}--color-image`}
                imageUrl={imageUrl}
                imageBaseUrl={imageBaseUrl}
                onChangeImage={(baseUrl) => props.setFormBackgroundImageBaseUrl(formId, baseUrl)}
                onEditImage={(url) => props.setFormBackgroundImageUrl(formId, url)}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${FORM_QA}--section-${CLOSE_BUTTON_LABEL_QA}`}>
            <SuiContainer className={'CloseButtonColorPicker'}>
                <SuiLabel text={CLOSE_BUTTON_LABEL} data-qa={`${FORM_QA}--label`} />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={props.closeButtonColor}
                    firstColorLabel={COLOR_PICKER_DEFAULT}
                    changeFirstColor={(id) => props.setFormCloseButtonColorDefault(formId, id)}
                    qaId={FORM_QA}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={props.closeButtonColorHover}
                    secondColor={props.closeButtonColorFocus}
                    firstColorLabel={COLOR_PICKER_HOVER}
                    secondColorLabel={COLOR_PICKER_FOCUS}
                    changeFirstColor={(id) => props.setFormCloseButtonColorHover(formId, id)}
                    changeSecondColor={(id) => props.setFormCloseButtonColorFocus(formId, id)}
                    qaId={`${FORM_QA}----hoverFocus`}
                />
            </SuiContainer>
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${FORM_QA}--section-${LINKS_LABEL_QA}`}>
            <SuiContainer className={'LinksColorPicker'}>
                <SuiLabel text={FORM_LINKS_LABEL} data-qa={`${FORM_QA}--label`} />
                <ColorPickerEditor
                    colorList={linksColorList}
                    firstColor={props.linksColor}
                    firstColorLabel={COLOR_PICKER_DEFAULT}
                    changeFirstColor={(id) => props.setFormLinksColorDefault(formId, id)}
                    qaId={`${FORM_QA}----links`}
                />
                <ColorPickerEditor
                    colorList={colorList}
                    firstColor={props.linksColorHover}
                    secondColor={props.linksColorFocus}
                    firstColorLabel={COLOR_PICKER_HOVER}
                    secondColorLabel={COLOR_PICKER_FOCUS}
                    changeFirstColor={(id) => props.setFormLinksColorHover(formId, id)}
                    changeSecondColor={(id) => props.setFormLinksColorFocus(formId, id)}
                    qaId={`${FORM_QA}----linksHoverFocus`}
                />
            </SuiContainer>
        </SuiContainer>
    </SuiContainer>
);

FormStyleEditorFormPanel.propTypes = {
    formId: PropTypes.string.isRequired,
    isRsvpEvent: PropTypes.bool.isRequired,
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    linksColorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    background: styleShapePropTypes,
    imageUrl: PropTypes.string,
    imageBaseUrl: PropTypes.string,
    displayMode: PropTypes.oneOf([DISPLAY_MODE_STANDARD, DISPLAY_MODE_PANEL, DISPLAY_MODE_FULL_SCREEN]),
    panelDisplay: PropTypes.oneOf([PANEL_DISPLAY_RIGHT, PANEL_DISPLAY_LEFT]),
    closeButtonColor: styleShapePropTypes,
    closeButtonColorHover: styleShapePropTypes,
    closeButtonColorFocus: styleShapePropTypes,
    linksColor: styleShapePropTypes,
    linksColorHover: styleShapePropTypes,
    linksColorFocus: styleShapePropTypes,
    setFormColorBackground: PropTypes.func.isRequired,
    setFormBackgroundImageBaseUrl: PropTypes.func.isRequired,
    setFormBackgroundImageUrl: PropTypes.func.isRequired,
    setFormDisplayMode: PropTypes.func.isRequired,
    setFormPanelDisplay: PropTypes.func.isRequired,
    setFormCloseButtonColorDefault: PropTypes.func.isRequired,
    setFormCloseButtonColorHover: PropTypes.func.isRequired,
    setFormCloseButtonColorFocus: PropTypes.func.isRequired,
    setFormLinksColorDefault: PropTypes.func.isRequired,
    setFormLinksColorHover: PropTypes.func.isRequired,
    setFormLinksColorFocus: PropTypes.func.isRequired,
};

FormStyleEditorFormPanel.defaultProps = {
    background: getDefaultStyle(DEFAULT_FORM_BACKGROUND_COLOR),
    imageUrl: null,
    imageBaseUrl: null,
    displayMode: DISPLAY_MODE_STANDARD,
    panelDisplay: PANEL_DISPLAY_LEFT,
    closeButtonColor: getDefaultStyle(DEFAULT_FORM_BUTTON_COLOR),
    closeButtonColorHover: getDefaultStyle(DEFAULT_FORM_BUTTON_COLOR_HOVER),
    closeButtonColorFocus: getDefaultStyle(DEFAULT_FORM_BUTTON_COLOR_FOCUS),
    linksColor: getDefaultStyle(DEFAULT_FORM_LINKS_COLOR),
    linksColorHover: getDefaultStyle(DEFAULT_FORM_LINKS_COLOR_HOVER),
    linksColorFocus: getDefaultStyle(DEFAULT_FORM_LINKS_COLOR_FOCUS),
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const linksColorList = [...colorList, { id: null, ...getDefaultStyle(DEFAULT_FORM_LINKS_COLOR) }];
    const formColors = getFormColors(state, formId);
    const { url: imageUrl, baseUrl: imageBaseUrl } = getFormBackgroundImage(state, formId);
    const { displayMode, panelDisplay } = getFormDisplayStyles(state, formId);
    const {
        default: closeButtonColor,
        hover: closeButtonColorHover,
        focus: closeButtonColorFocus,
    } = getFormCloseButtonStyles(state, formId);
    const { default: linksColor, hover: linksColorHover, focus: linksColorFocus } = getFormLinksStyles(state, formId);
    const formType = getFormType(state, formId);
    const isRsvpEvent = formType === FORM_TYPE_RSVP;

    return {
        colorList,
        linksColorList,
        formId,
        imageUrl,
        imageBaseUrl,
        displayMode,
        panelDisplay,
        closeButtonColor,
        closeButtonColorHover,
        closeButtonColorFocus,
        linksColor,
        linksColorHover,
        linksColorFocus,
        ...formColors,
        isRsvpEvent,
    };
};

const mapDispatchToProps = {
    setFormColorBackground,
    setFormBackgroundImageBaseUrl,
    setFormBackgroundImageUrl,
    setFormDisplayMode,
    setFormPanelDisplay,
    setFormCloseButtonColorDefault,
    setFormCloseButtonColorHover,
    setFormCloseButtonColorFocus,
    setFormLinksColorDefault,
    setFormLinksColorHover,
    setFormLinksColorFocus,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleEditorFormPanel);
